import React, { useState } from "react";
import { Alert, Button, Divider, Form, Typography } from "antd";
import { KeyOutlined, SafetyOutlined } from "@ant-design/icons";
import { fetchPasskeyLoginOptions, verifyMfaChallenge, verifyPasskeyLogin } from "../../api/auth/mfaApi";
import { finishSignIn, type SignInResult } from "../../utils/auth/sessionUtilities";
import { attemptsRemaining, authFailure, authFailureMessage } from "../../lib/http/authErrors";
import { getPasskeyAssertion, isPasskeySupported, PasskeyCeremonyError, passkeyFailureMessage } from "../../lib/webauthn/webauthn";
import type { LoginMfaRequired } from "../../lib/http/session";
import OtpInput from "../../components/auth/mfa/OtpInput";
import { isOtp, normaliseOtp } from "../../components/auth/mfa/codes";

const { Paragraph } = Typography;

export const CHALLENGE_EXPIRED_MESSAGE = "Your sign-in attempt expired. Please start again.";

interface SecondFactorStepProps {
	challenge: LoginMfaRequired;
	onResult: (result: SignInResult) => void;
	onRestart: (message?: string) => void;
	onLostDevice: () => void;
}

const secondFactorError = (error: unknown, method: "totp" | "passkey"): string => {
	if (error instanceof PasskeyCeremonyError) return passkeyFailureMessage(error.reason);
	const failure = authFailure(error);
	if (failure.code === "invalid_credentials") {
		const base = method === "passkey" ? "That passkey is not registered to this account." : "That code is incorrect.";
		const left = attemptsRemaining(failure);
		return left === undefined ? base : `${base} ${left} ${left === 1 ? "attempt" : "attempts"} left.`;
	}
	return authFailureMessage(error);
};

// The mfa_required continuation (contract §6.3): an authenticator code or a passkey against the challenge.
const SecondFactorStep: React.FC<SecondFactorStepProps> = ({ challenge, onResult, onRestart, onLostDevice }) => {
	const [form] = Form.useForm<{ code: string }>();
	const [busy, setBusy] = useState<"totp" | "passkey" | null>(null);
	const [error, setError] = useState("");
	const offersTotp = challenge.methods.includes("totp");
	const offersPasskey = challenge.methods.includes("passkey") && isPasskeySupported();

	const fail = (err: unknown, method: "totp" | "passkey") => {
		if (authFailure(err).code === "challenge_expired") {
			onRestart(CHALLENGE_EXPIRED_MESSAGE);
			return;
		}
		setError(secondFactorError(err, method));
	};

	const submitCode = async ({ code }: { code: string }) => {
		setBusy("totp");
		setError("");
		try {
			onResult(await finishSignIn(await verifyMfaChallenge(challenge.challenge_id, normaliseOtp(code))));
		} catch (err) {
			form.setFieldValue("code", "");
			fail(err, "totp");
		} finally {
			setBusy(null);
		}
	};

	const confirmWithPasskey = async () => {
		setBusy("passkey");
		setError("");
		try {
			const { publicKey, flow_id } = await fetchPasskeyLoginOptions(challenge.challenge_id);
			const credential = await getPasskeyAssertion(publicKey);
			onResult(await finishSignIn(await verifyPasskeyLogin(flow_id, credential)));
		} catch (err) {
			fail(err, "passkey");
		} finally {
			setBusy(null);
		}
	};

	return (
		<div className="login-form" data-testid="login-mfa-step">
			<Paragraph className="login-step-intro">
				{offersTotp ? "Enter the 6-digit code from your authenticator app." : "Confirm with the passkey on this account."}
			</Paragraph>

			{error && (
				<div className="login-form-item">
					<Alert data-testid="login-mfa-error" message={error} type="error" showIcon />
				</div>
			)}

			{offersTotp && (
				<Form form={form} name="mfa" layout="vertical" size="large" onFinish={submitCode} requiredMark={false}>
					<Form.Item
						name="code"
						label="Authentication code"
						className="login-form-item"
						rules={[{ validator: (_, value) => (isOtp(value ?? "") ? Promise.resolve() : Promise.reject(new Error("Enter the 6-digit code"))) }]}
					>
						<OtpInput data-testid="login-mfa-code" prefix={<SafetyOutlined />} autoFocus disabled={busy !== null} />
					</Form.Item>
					<Form.Item className="login-form-item">
						<Button data-testid="login-mfa-submit" type="primary" htmlType="submit" block loading={busy === "totp"} disabled={busy === "passkey"}>
							Verify
						</Button>
					</Form.Item>
				</Form>
			)}

			{offersTotp && offersPasskey && <Divider plain>or</Divider>}

			{offersPasskey && (
				<div className="login-form-item">
					<Button data-testid="login-mfa-passkey" icon={<KeyOutlined />} block size="large" onClick={confirmWithPasskey} loading={busy === "passkey"} disabled={busy === "totp"} type={offersTotp ? "default" : "primary"}>
						Use a passkey
					</Button>
				</div>
			)}

			<div className="login-step-links">
				{challenge.recovery_methods.length > 0 && (
					<Button data-testid="login-mfa-lost-device" type="link" onClick={onLostDevice} disabled={busy !== null}>
						Lost your device?
					</Button>
				)}
				<Button data-testid="login-mfa-back" type="link" onClick={() => onRestart()} disabled={busy !== null}>
					Back to sign in
				</Button>
			</div>
		</div>
	);
};

export default SecondFactorStep;
